import { useEffect, useMemo } from "react";
import type {
  Block,
  TableBlockData,
  TableColumn,
  TableRow,
  TextPart,
} from "../../../../models/ManualAuthoring";
import {
  buildHeaderRows,
  getLeafColumnWidthMap,
  getLeafColumns,
} from "../../tableBuilderUtils/previewUtils";
import { useManualAuthoringStore } from "../../../../store/manualAuthoringStore";
import "./tableBlock.css";

type Props = {
  block: Block;
  tableNumber?: number;
  onDone: () => void;
};

function serializeCellValue(parts: TextPart[]): string {
  return parts
    .map((part) =>
      part.type === "text"
        ? part.value
        : `[[REF:${part.targetId}|${part.label}]]`
    )
    .join("");
}

function parseCellText(value: string): TextPart[] {
  const regex = /\[\[REF:([^\]|]+)(?:\|([^\]]*))?\]\]/g;
  const parts: TextPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: "text",
        value: value.slice(lastIndex, match.index),
      });
    }

    parts.push({
      type: "ref",
      targetId: match[1],
      label: match[2]?.trim() || match[1],
    });

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < value.length) {
    parts.push({
      type: "text",
      value: value.slice(lastIndex),
    });
  }

  return parts;
}

function createEmptyRow(columns: TableColumn[]): TableRow {
  return {
    cells: columns.map((column) => ({
      key: column.key || column.id,
      value: [{ type: "text", value: "" }],
      align: column.align,
    })),
  };
}

function upsertCell(
  row: TableRow,
  key: string,
  value: TextPart[],
  align?: "left" | "center" | "right"
): TableRow {
  const index = row.cells.findIndex((cell) => cell.key === key);
  if (index < 0) {
    return {
      ...row,
      cells: [...row.cells, { key, value, align }],
    };
  }

  const cells = [...row.cells];
  cells[index] = { ...cells[index], value };
  return { ...row, cells };
}

export default function TableEdit({ block, tableNumber, onDone }: Props) {
  const updateBlock = useManualAuthoringStore((s) => s.updateBlock);
  const data = block.data as TableBlockData;

  const rows = data.rows || [];
  const leafColumns = useMemo(
    () => getLeafColumns(data.columns ?? []),
    [data.columns]
  );
  const headerRows = useMemo(
    () => buildHeaderRows(data.columns ?? []),
    [data.columns]
  );
  const leafWidthMap = useMemo(
    () => getLeafColumnWidthMap(data.columns ?? []),
    [data.columns]
  );

  useEffect(() => {
    if (rows.length === 0 && leafColumns.length > 0) {
      const rowCount = Math.max(1, data.defaultRows ?? 1);
      const initialRows = Array.from({ length: rowCount }, () =>
        createEmptyRow(leafColumns)
      );
      updateBlock(block.id, { rows: initialRows });
    }
  }, [block.id, data.defaultRows, leafColumns, rows.length, updateBlock]);

  const updateCell = (
    rowIndex: number,
    key: string,
    value: string,
    align?: "left" | "center" | "right"
  ) => {
    const updatedRows = [...rows];
    const currentRow = updatedRows[rowIndex] || { cells: [] };
    updatedRows[rowIndex] = upsertCell(
      currentRow,
      key,
      parseCellText(value),
      align
    );
    updateBlock(block.id, { rows: updatedRows });
  };

  const addRow = () => {
    updateBlock(block.id, {
      rows: [...rows, createEmptyRow(leafColumns)],
    });
  };

  const removeRow = (rowIndex: number) => {
    const updatedRows = rows.filter((_, i) => i !== rowIndex);
    updateBlock(block.id, { rows: updatedRows });
  };

  const justifyContent =
    data.alignment === "center"
      ? "center"
      : data.alignment === "right"
        ? "flex-end"
        : "flex-start";

  if (!leafColumns.length) {
    return <div className="pdf-block pdf-table-block__empty">No table columns configured.</div>;
  }

  const tableTitle = data.displayTitle || data.templateName || "Untitled Table";
  const titleText = tableNumber ? `Table ${tableNumber}. ${tableTitle}` : tableTitle;

  return (
    <div className="pdf-block pdf-table-block">
      <div className="pdf-table-block__title">{titleText}</div>

      <div className="pdf-table-block__edit-head">
        <div className="pdf-table-block__actions">
          <button className="btn btn-sm btn-outline-primary" type="button" onClick={addRow}>
            + Add Row
          </button>
          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={onDone}>
            Done
          </button>
        </div>
      </div>

      <div className="pdf-table-block__shell" style={{ justifyContent }}>
        <table className="pdf-table-block__table" style={{ width: data.width || "100%" }}>
          <colgroup>
            {leafColumns.map((column) => (
              <col
                key={`edit-col-${column.id}`}
                style={
                  leafWidthMap[column.id]
                    ? { width: `${leafWidthMap[column.id]}%` }
                    : undefined
                }
              />
            ))}
            <col style={{ width: "38px" }} />
          </colgroup>
          <thead>
            {headerRows.map((row, rowIndex) => (
              <tr key={`head-${rowIndex}`}>
                {row.map((cell) => (
                  <th
                    key={cell.id}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                    style={cell.width ? { width: `${cell.width}%` } : undefined}
                  >
                    {cell.header}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {(rows.length ? rows : [createEmptyRow(leafColumns)]).map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {leafColumns.map((column) => {
                  const key = column.key || column.id;
                  const value = serializeCellValue(
                    row.cells.find((cell) => cell.key === key)?.value || []
                  );
                  return (
                    <td
                      key={`${rowIndex}-${key}`}
                      style={
                        column.align
                          ? { textAlign: column.align as "left" | "center" | "right" }
                          : undefined
                      }
                    >
                      {column.wrap ? (
                        <textarea
                          className="pdf-table-block__input pdf-table-block__textarea"
                          value={value}
                          onChange={(e) =>
                            updateCell(rowIndex, key, e.target.value, column.align)
                          }
                          rows={2}
                        />
                      ) : (
                        <input
                          className="pdf-table-block__input"
                          value={value}
                          onChange={(e) =>
                            updateCell(rowIndex, key, e.target.value, column.align)
                          }
                        />
                      )}
                    </td>
                  );
                })}
                <td className="pdf-table-block__row-action">
                  <button
                    className="btn btn-sm btn-outline-danger"
                    type="button"
                    onClick={() => removeRow(rowIndex)}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
